import { LocalizationRes, SingleDateTimePicker, SingleDateTimePickerProps, TimePicker } from "@centricsoftware/design-system";
import { OPEN_RES } from "csi/components/constants";
import { convertTimeTo12HrFormat, FORMAT_DATE, FORMAT_DATETIME, FORMAT_DATETIME_SECONDS, FORMAT_DATETIME_ZONE, FORMAT_TIME, FORMAT_UNIVERSAL_DATE, getHoursAndMinutes } from "csi/utility/dateUtility";
import React, { ChangeEvent, FC } from "react";
import { SelectSingleEventHandler } from "react-day-picker";

export type TimeComponentProps = SingleDateTimePickerProps & {
	calendarButtonTooltip?: string;
	format: string;
	invalid?: boolean;
	label?: string;
	labelRes?: LocalizationRes;
	message?: string;
	onBlurChange?: (value: Date | string) => void;
	required?: boolean;
	value?: Date | string;
};

/**
 * TODO: Consider supporting the following formats:
 * "Date",
 * "Time",
 * "DateTime",
 * "DateTimeSeconds",
 * "DateTimeZone",
 * "UniversalDate",
 * -- Maybe add support later -- :
 * "Diff",
 * "UniversalTime"
 */

const SUPPORTED_FORMATS = [
	"",
	FORMAT_DATE,
	FORMAT_TIME,
	FORMAT_DATETIME,
	FORMAT_DATETIME_SECONDS,
	FORMAT_DATETIME_ZONE,
	// FORMAT_DIFF,
	FORMAT_UNIVERSAL_DATE,
	// FORMAT_UNIVERSAL_TIME,
];

const TIME_SUPPORTED_FORMATS = [
	FORMAT_TIME,
	FORMAT_DATETIME,
	FORMAT_DATETIME_SECONDS,
];

export const TimeComponent: FC<TimeComponentProps> = (({
	format,
	invalid,
	message,
	onBlurChange,
	onChange,
	value,
	...props
}) => {
	if (!format) {
		format = FORMAT_DATETIME;
	}

	if (!SUPPORTED_FORMATS.includes(format)) {
		console.warn(`Unsupported time format: ${format}`);
		return null;
	}

	if (format === FORMAT_TIME) {
		const onChangeCallback = (date: Date): void => {
			onBlurChange?.(date);
			onChange?.({ target: { value: date as Date | string } } as ChangeEvent<HTMLInputElement>);
		};

		let time;
		if (value) {
			const date = new Date(value as string);
			const hoursAndMinutes = getHoursAndMinutes(date);
			time = convertTimeTo12HrFormat(hoursAndMinutes);
		}

		return (
			<TimePicker
				{...props}
				date={new Date(1970, 0, 1, 0, 0, 0)}
				defaultTime={time}
				error={invalid}
				errorMessage={message}
				onChange={onChangeCallback}
			/>
		);
	}

	let selected;
	if (value) {
		selected = new Date(value);
	}

	const onSelect: SelectSingleEventHandler = (date: Date): void => {
		onBlurChange?.(date);
		onChange?.({ target: { value: date as Date | string } } as ChangeEvent<HTMLInputElement>);
	};

	return (
		<SingleDateTimePicker
			calendarButtonTooltipRes={OPEN_RES}
			{...props}
			error={invalid}
			errorText={message}
			onSelect={onSelect}
			selected={selected}
			showTimePicker={TIME_SUPPORTED_FORMATS.includes(format)}
		/>
	);
});
